import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ShieldCheck } from "lucide-react";
import { NAV, COMPANY } from "@/lib/site-data";
import { motion, AnimatePresence } from "framer-motion";

const Logo = () => (
    <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
        <img
            src="https://customer-assets.emergentagent.com/job_charge-everywhere-2/artifacts/ax26z8mx_Logo.png"
            alt="M-Class EV Charging"
            className="w-10 h-10 object-contain"
        />
        <div className="leading-tight">
            <div className="font-heading font-semibold text-white tracking-tight text-[15px]">M-Class</div>
            <div className="text-[10px] text-electric-400 tracking-[0.25em] uppercase">EV Charging</div>
        </div>
    </Link>
);

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [openSub, setOpenSub] = useState(null);
    const loc = useLocation();

    useEffect(() => { setOpen(false); setOpenSub(null); }, [loc.pathname]);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-ink-900/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
            }`}
        >
            {/* Veteran-owned strip */}
            <div className="hidden md:flex items-center justify-between px-8 lg:px-12 h-8 border-b border-white/5 text-[11px] tracking-[0.18em] uppercase">
                <div className="flex items-center gap-2 text-slate-400">
                    <ShieldCheck className="w-3.5 h-3.5 text-electric-400" />
                    <span data-testid="veteran-badge">Veteran-Owned · Texas & Nationwide</span>
                </div>
                <a href={`tel:${COMPANY.phone}`} data-testid="header-phone-link" className="flex items-center gap-2 text-slate-400 hover:text-electric-400 transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {COMPANY.phone}
                </a>
            </div>

            <div className="flex items-center justify-between px-6 md:px-8 lg:px-12 h-16">
                <Logo />

                <nav className="hidden lg:flex items-center gap-6">
                    {NAV.map((item) => (
                        <div key={item.label} className="relative group" onMouseEnter={() => setOpenSub(item.label)} onMouseLeave={() => setOpenSub(null)}>
                            <NavLink
                                to={item.to}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-1 text-[12px] uppercase tracking-[0.18em] transition-colors py-2 ${
                                        isActive ? "text-electric-400" : "text-slate-300 hover:text-electric-400"
                                    }`
                                }
                            >
                                {item.label}
                                {item.children && <ChevronDown className="w-3 h-3" />}
                            </NavLink>
                            {item.children && openSub === item.label && (
                                <div className="absolute top-full left-0 pt-2 min-w-[240px]">
                                    <div className="bg-ink-700/95 backdrop-blur-xl border border-white/10 py-2">
                                        {item.children.map((c) => (
                                            <Link
                                                key={c.label}
                                                to={c.to}
                                                data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                                                className="block px-4 py-2.5 text-[12px] uppercase tracking-[0.16em] text-slate-300 hover:text-electric-400 hover:bg-white/5"
                                            >{c.label}</Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link to="/contact" data-testid="header-cta-consultation" className="btn-primary text-[11px] py-3 px-5">Request Consultation</Link>
                </div>

                <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu">
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                        className="lg:hidden bg-ink-900/95 backdrop-blur-xl border-t border-white/10 max-h-[80vh] overflow-y-auto"
                    >
                        <div className="px-6 py-6 space-y-1">
                            {NAV.map((item) => (
                                <div key={item.label}>
                                    <Link to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase()}`} className="block py-3 text-sm uppercase tracking-[0.18em] text-slate-200 hover:text-electric-400 border-b border-white/5">{item.label}</Link>
                                    {item.children && (
                                        <div className="pl-4 py-2 space-y-1">
                                            {item.children.map((c) => (
                                                <Link key={c.label} to={c.to} className="block py-1.5 text-xs uppercase tracking-[0.16em] text-slate-400 hover:text-electric-400">{c.label}</Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <Link to="/contact" data-testid="mobile-cta-consultation" className="btn-primary w-full mt-4">Request Consultation</Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
